import React, { useState, useEffect } from 'react';
import apiClient from '../../lib/apiClient';
import { Button } from '../ui/button';
import { FileText, Plus, Edit2, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const CVManager = () => {
  const { toast } = useToast();
  const [cvLinks, setCvLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    role: '',
    url: '',
  });

  const cvRoles = [
    'Security',
    'SDE',
    'Mobile Application Development',
    'Frontend',
    'Cloud Engineer',
  ];

  useEffect(() => {
    const loadCVLinks = async () => {
      try {
        setLoading(true);
        const data = await apiClient.get('/api/admin/cv-links');
        setCvLinks(data);
      } catch (error) {
        console.error('Failed to load CV links:', error);
        toast({
          title: 'Error',
          description: 'Failed to load CV links',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadCVLinks();
  }, [toast]);

  const handleAdd = () => {
    setFormData({ role: '', url: '' });
    setEditingRole(null);
    setIsEditing(true);
  };

  const handleEdit = (role) => {
    const existing = cvLinks.find((link) => link.role === role);
    if (existing) {
      setFormData({ role: existing.role, url: existing.url });
      setEditingRole(existing.role);
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!formData.role || !formData.url) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    // Validate URL
    try {
      new URL(formData.url);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Please enter a valid URL',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingRole) {
        // Update existing
        const updated = await apiClient.put(
          `/api/admin/cv-links/${editingRole}`,
          { url: formData.url }
        );
        setCvLinks((prev) =>
          prev.map((link) => (link.role === editingRole ? updated : link))
        );
        toast({
          title: 'Success',
          description: 'CV link updated successfully',
        });
      } else {
        // Create new
        const newLink = await apiClient.post('/api/admin/cv-links', formData);
        setCvLinks((prev) => [...prev, newLink]);
        toast({
          title: 'Success',
          description: 'CV link created successfully',
        });
      }
      setIsEditing(false);
      setFormData({ role: '', url: '' });
    } catch (error) {
      console.error('Failed to save CV link:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save CV link',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete CV link for ${role}?`)) return;

    try {
      await apiClient.delete(`/api/admin/cv-links/${role}`);
      setCvLinks((prev) => prev.filter((link) => link.role !== role));
      toast({
        title: 'Success',
        description: 'CV link deleted successfully',
      });
    } catch (error) {
      console.error('Failed to delete CV link:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete CV link',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cv-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-cyan-500" />
          <h1 className="text-3xl font-bold text-white">CV Links Management</h1>
        </div>
        {!isEditing && (
          <Button
            onClick={handleAdd}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add CV Link
          </Button>
        )}
      </div>

      {isEditing && (
        <div className="bg-neutral-900 border border-cyan-500/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {editingRole ? `Edit CV Link - ${editingRole}` : 'Add New CV Link'}
            </h2>
            <button
              onClick={() => {
                setIsEditing(false);
                setFormData({ role: '', url: '' });
              }}
              className="text-neutral-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {!editingRole && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  <option value="">Select a role</option>
                  {cvRoles.map((role) => (
                    <option
                      key={role}
                      value={role}
                      disabled={cvLinks.some((link) => link.role === role)}
                    >
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Google Drive Link (with open permission)
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="https://drive.google.com/file/d/FILE_ID/view?usp=sharing"
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-cyan-500 focus:outline-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSave}
                className="bg-cyan-500 hover:bg-cyan-600 text-black"
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({ role: '', url: '' });
                }}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CV Links List */}
      <div className="space-y-3">
        {cvLinks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-neutral-400">
              No CV links added yet. Add one to get started.
            </p>
          </div>
        ) : (
          cvLinks.map((link) => (
            <div
              key={link.role}
              className="flex items-center justify-between bg-neutral-900 border border-neutral-800 rounded-lg p-4 hover:border-cyan-500/50 transition-colors"
            >
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">
                  {link.role}
                </h3>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors truncate"
                >
                  {link.url}
                </a>
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  onClick={() => handleEdit(link.role)}
                  variant="outline"
                  size="sm"
                  className="border-neutral-700 text-white hover:bg-neutral-800"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleDelete(link.role)}
                  variant="outline"
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CVManager;
